import { useEffect, useState } from 'react';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Guard for environments where `window` is not available (e.g., SSR)
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    // Cleanup the listener on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preload hero image
  useEffect(() => {
    const img = new Image();
    img.src = 'https://public.readdy.ai/ai/img_res/ab50abfa11b92aae707082136ea15954.jpg';
    img.onload = () => setImageLoaded(true);
  }, []);

  // 滚动到联系区域
  const openCalendly = () => {
    window.open('https://calendly.com/chanelle-proeditionssc/30min', '_blank');
  };

  // 滚动到专业服务区域
  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动互动效果计算
  const scrollProgress = Math.min(scrollY / 800, 1);
  const heroOpacity = Math.max(0.2, 1 - scrollProgress * 0.8);
  const heroScale = Math.max(0.9, 1 - scrollProgress * 0.1);
  const contentTranslateY = scrollProgress * 50;
  const overlayOpacity = Math.min(0.7, 0.4 + scrollProgress * 0.3);

  return (
    <section 
      className="relative min-h-screen flex items-center justify-start overflow-hidden z-10"
      aria-labelledby="hero-heading"
      style={{
        opacity: heroOpacity,
        transform: `scale(${heroScale})`,
        transition: 'opacity 0.1s ease-out, transform 0.1s ease-out'
      }}
    >
      {/* 背景图片 */}
      <div
        className={`absolute inset-0 w-full h-full z-0 transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        role="img"
        aria-label="Community celebration at school ribbon cutting ceremony in South Carolina"
        style={{
          backgroundImage: imageLoaded ? `url('https://readdy.ai/api/search-image?query=Dynamic%20cinematic%20shot%20of%20diverse%20community%20celebrating%20school%20ribbon%20cutting%20ceremony%20in%20South%20Carolina%2C%20featuring%20Black%20woman%20leader%20prominently%20in%20foreground%20holding%20large%20ceremonial%20golden%20scissors%20with%20confident%20smile%2C%20she%20is%20wearing%20elegant%20business%20casual%20suit%20with%20blazer%20and%20has%20straight%20sleek%20hair%2C%20surrounded%20by%20diverse%20group%20of%20educators%20and%20community%20members%20in%20motion%20showing%20genuine%20excitement%20and%20celebration%2C%20people%20clapping%20and%20cheering%2C%20red%20ribbon%20ready%20to%20be%20cut%2C%20warm%20golden%20hour%20lighting%2C%20professional%20documentary%20photography%20style%20capturing%20authentic%20energy%20and%20movement%2C%20modern%20school%20architectural%20rendering%20visible%20on%20display%20board%2C%20green%20trees%20and%20blue%20sky%20background&width=1920&height=1080&seq=hero-diverse-ribbon-cutting-scissors-002&orientation=landscape')` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1a4d2e'
        }}
      ></div>

      {/* Loading skeleton for background */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 animate-pulse z-0"></div>
      )}

      {/* 动态深色遮罩 */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 z-10"
        aria-hidden="true"
        style={{
          opacity: overlayOpacity,
          transition: 'opacity 0.1s ease-out',
        }}
      ></div>

      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="max-w-2xl"
          style={{
            transform: `translateY(${contentTranslateY}px)`,
            transition: 'transform 0.1s ease-out',
          }}
        >
          <div className="space-y-8">
            <div className="space-y-6">
              <div
                className="inline-block"
                style={{
                  transform: `translateY(${contentTranslateY * 0.6}px)`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                <span className="text-sm lg:text-base tracking-widest text-white font-medium uppercase drop-shadow-lg">
                  EXPERT BUSINESS WRITING SERVICES
                </span>
              </div>
              
              <h1
                id="hero-heading"
                className="text-5xl lg:text-6xl xl:text-7xl font-light text-white leading-tight drop-shadow-xl"
                style={{
                  transform: `translateY(${contentTranslateY * 0.5}px)`,
                  transition: 'transform 0.1s ease-out',
                  textShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                }}
              >
                Transforming Content into{' '}
                <span className="font-normal">Profit</span>
              </h1>

              <p
                className="text-xl lg:text-2xl text-white leading-relaxed font-light max-w-xl drop-shadow-lg"
                style={{
                  transform: `translateY(${contentTranslateY * 0.3}px)`,
                  transition: 'transform 0.1s ease-out',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                }}
              >
                ProEditions is the human touch that small businesses and non-profit organizations deserve.
              </p>
            </div>

            <div
              className="flex flex-col sm:flex-row gap-6"
              style={{
                transform: `translateY(${contentTranslateY * 0.2}px)`,
                opacity: Math.max(0.3, 1 - scrollProgress * 0.7),
                transition:
                  'transform 0.1s ease-out, opacity 0.1s ease-out',
              }}
            >
              <button 
                onClick={openCalendly}
                className="bg-white text-gray-900 px-10 py-5 text-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer rounded-full focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50"
                aria-label="Schedule a free consultation with ProEditions"
              >
                Schedule a free consultation
              </button>
              <button 
                onClick={scrollToServices}
                className="px-10 py-5 text-lg font-light text-white border-2 border-white/50 hover:border-white hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer rounded-full focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50"
                aria-label="Learn more about ProEditions services"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 滚动指示器 */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        aria-hidden="true"
        style={{
          opacity: Math.max(0, 1 - scrollProgress * 2),
          transition: 'opacity 0.2s ease-out',
        }}
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-white/70 text-sm font-light">
            Scroll to explore
          </span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
}